import { useRef, useState } from "react";
import { Scale, Calculator, Users, CalendarHeart, Megaphone, MonitorSmartphone, Send, FileText } from "lucide-react";
import { toast } from "sonner";
import { postBenevole, uploadBenevoleDocument } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const durees = ["Quelques semaines", "Quelques mois", "6 mois à 1 an", "1 an et plus", "Engagement continu"];

const domaines = [
  { id: "Droit", icone: Scale, titre: "Droit", texte: "Avocat·es, notaires et juristes : cliniques d'information, ateliers et révision de contenus." },
  { id: "Fiscalité et comptabilité", icone: Calculator, titre: "Fiscalité et comptabilité", texte: "CPA et fiscalistes : ateliers d'impôt, vérification de gabarits, accompagnement des entrepreneurs." },
  { id: "Mentorat", icone: Users, titre: "Mentorat", texte: "Entrepreneur·es d'expérience : jumelage avec des porteurs de projet, dont la cohorte Entrepreneurs Résilients." },
  { id: "Événements", icone: CalendarHeart, titre: "Événements", texte: "Logistique, accueil et organisation de nos formations, conférences et remises de diplômes." },
  { id: "Communications", icone: Megaphone, titre: "Communications", texte: "Rédaction, traduction, réseaux sociaux et vulgarisation de nos contenus juridiques et fiscaux." },
  { id: "Technologie", icone: MonitorSmartphone, titre: "Technologie", texte: "Soutien technique de nos plateformes de formation en ligne et de nos outils numériques." },
];

const inputCls =
  "w-full rounded-sm border border-input bg-background px-4 py-3.5 text-sm outline-none transition-colors duration-200 focus:border-primary focus:ring-1 focus:ring-primary";

export default function Benevole() {
  const [form, setForm] = useState({
    nom: "", courriel: "", telephone: "", disponibilites: "", message: "",
    duree_engagement: "", services_similaires: "", services_similaires_details: "",
    reference1_nom: "", reference1_contact: "", reference2_nom: "", reference2_contact: "",
  });
  const [choisis, setChoisis] = useState([]);
  const [cv, setCv] = useState(null);
  const [lettre, setLettre] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const cvRef = useRef(null);
  const lettreRef = useRef(null);

  const update = (champ) => (e) => setForm({ ...form, [champ]: e.target.value });

  const basculer = (id) =>
    setChoisis((prev) => (prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]));

  const choisirFichier = (setter) => (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) {
      toast.error("Fichier trop volumineux", { description: "Chaque document doit faire au maximum 20 Mo." });
      e.target.value = "";
      return;
    }
    setter(f);
  };

  const soumettre = async (e) => {
    e.preventDefault();
    if (choisis.length === 0) {
      toast.error("Choisissez au moins un domaine", { description: "Indiquez où vous aimeriez contribuer." });
      return;
    }
    if (!cv || !lettre) {
      toast.error("Documents manquants", { description: "Joignez votre CV et votre lettre de présentation." });
      return;
    }
    setEnvoi(true);
    try {
      const [cvDoc, lettreDoc] = await Promise.all([
        uploadBenevoleDocument("cv", cv),
        uploadBenevoleDocument("lettre_presentation", lettre),
      ]);
      await postBenevole({
        ...form,
        telephone: form.telephone || null,
        disponibilites: form.disponibilites || null,
        services_similaires_details: form.services_similaires === "oui" ? form.services_similaires_details || null : null,
        domaines: choisis.join(", "),
        cv_document_id: cvDoc.document_id,
        lettre_document_id: lettreDoc.document_id,
      });
      toast.success("Candidature envoyée!", {
        description: "Merci de votre engagement. Notre équipe vous contactera très bientôt.",
      });
      setForm({
        nom: "", courriel: "", telephone: "", disponibilites: "", message: "",
        duree_engagement: "", services_similaires: "", services_similaires_details: "",
        reference1_nom: "", reference1_contact: "", reference2_nom: "", reference2_contact: "",
      });
      setChoisis([]);
      setCv(null);
      setLettre(null);
    } catch {
      toast.error("Une erreur est survenue", { description: "Votre candidature n'a pas pu être envoyée. Veuillez réessayer." });
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <>
      <section className="bg-espresso py-28 text-ivory" data-testid="benevole-hero">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Devenir bénévole</p>
            <h1 className="mt-6 max-w-3xl font-serif text-5xl font-medium leading-[1.1] tracking-tight md:text-6xl" data-testid="benevole-title">
              Votre temps et votre expertise peuvent <span className="italic text-primary">lever des barrières</span>.
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-ivory/70 md:text-lg">
              Juristes, fiscalistes, comptables, mentors, communicants ou simples passionnés : nos
              bénévoles sont au cœur de la mission. Inscrivez-vous à notre liste de bénévoles et
              nous vous contacterons selon vos domaines et vos disponibilités.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-background py-20 md:py-28" data-testid="benevole-domaines">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Où contribuer</p>
            <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.15] tracking-tight md:text-5xl">
              Six façons de vous impliquer
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden border border-border bg-border md:grid-cols-2 lg:grid-cols-3">
            {domaines.map((d, i) => (
              <Reveal key={d.id} delay={Math.min(i * 0.06, 0.25)} className="bg-background">
                <div className="h-full p-10 transition-colors duration-300 hover:bg-card" data-testid={`benevole-domaine-${i}`}>
                  <d.icone size={28} strokeWidth={1.5} className="text-primary" />
                  <h3 className="mt-6 font-serif text-2xl font-semibold">{d.titre}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-foreground/60">{d.texte}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-border bg-card py-20 md:py-28" data-testid="benevole-form-section">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl font-semibold tracking-tight md:text-4xl" data-testid="benevole-form-title">
              Rejoindre la liste des bénévoles
            </h2>
            <form onSubmit={soumettre} className="mt-10 space-y-6" data-testid="benevole-form">
              <div>
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Domaines de contribution *</p>
                <div className="flex flex-wrap gap-3" data-testid="benevole-domaines-choix">
                  {domaines.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => basculer(d.id)}
                      data-testid={`benevole-chip-${d.id.toLowerCase().replace(/[^a-z]/g, "-")}`}
                      className={`rounded-sm border px-4 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                        choisis.includes(d.id)
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-background text-foreground/70 hover:border-primary hover:text-primary"
                      }`}
                    >
                      {d.titre}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="b-nom" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Nom complet *</label>
                  <input id="b-nom" required minLength={2} value={form.nom} onChange={update("nom")} className={inputCls} placeholder="Votre nom" data-testid="benevole-nom-input" />
                </div>
                <div>
                  <label htmlFor="b-courriel" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Courriel *</label>
                  <input id="b-courriel" type="email" required value={form.courriel} onChange={update("courriel")} className={inputCls} placeholder="vous@exemple.ca" data-testid="benevole-courriel-input" />
                </div>
              </div>
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="b-tel" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Téléphone</label>
                  <input id="b-tel" type="tel" value={form.telephone} onChange={update("telephone")} className={inputCls} placeholder="514 000-0000" data-testid="benevole-telephone-input" />
                </div>
                <div>
                  <label htmlFor="b-dispo" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Disponibilités</label>
                  <input id="b-dispo" value={form.disponibilites} onChange={update("disponibilites")} className={inputCls} placeholder="Ex. : soirs de semaine, 4 h/mois" data-testid="benevole-dispo-input" />
                </div>
              </div>
              <div>
                <label htmlFor="b-duree" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Durée de votre engagement *</label>
                <select id="b-duree" required value={form.duree_engagement} onChange={update("duree_engagement")} className={inputCls} data-testid="benevole-duree-select">
                  <option value="" disabled>Choisissez une durée</option>
                  {durees.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Avez-vous déjà offert des services similaires? *</p>
                <div className="flex gap-3" data-testid="benevole-similaires-choix">
                  {[{ v: "oui", l: "Oui" }, { v: "non", l: "Non" }].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setForm({ ...form, services_similaires: o.v })}
                      data-testid={`benevole-similaires-${o.v}`}
                      className={`rounded-sm border px-5 py-2.5 text-sm font-semibold transition-colors duration-200 ${
                        form.services_similaires === o.v
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-background text-foreground/70 hover:border-primary hover:text-primary"
                      }`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
                {form.services_similaires === "oui" && (
                  <textarea
                    rows={3}
                    value={form.services_similaires_details}
                    onChange={update("services_similaires_details")}
                    className={`${inputCls} mt-4 resize-y`}
                    placeholder="Précisez : auprès de quelles organisations, dans quelles fonctions…"
                    data-testid="benevole-similaires-details"
                  />
                )}
              </div>
              <div>
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Deux références (pour valider votre implication antérieure) *</p>
                <div className="space-y-4">
                  {[1, 2].map((n) => (
                    <div key={n} className="grid gap-4 rounded-sm border border-border bg-background p-4 md:grid-cols-2" data-testid={`benevole-reference-${n}`}>
                      <div>
                        <label htmlFor={`b-ref${n}-nom`} className="mb-2 block text-xs font-semibold text-foreground/60">Référence {n} — Nom complet *</label>
                        <input id={`b-ref${n}-nom`} required minLength={2} value={form[`reference${n}_nom`]} onChange={update(`reference${n}_nom`)} className={inputCls} placeholder="Nom de la personne" data-testid={`benevole-ref${n}-nom-input`} />
                      </div>
                      <div>
                        <label htmlFor={`b-ref${n}-contact`} className="mb-2 block text-xs font-semibold text-foreground/60">Courriel ou téléphone *</label>
                        <input id={`b-ref${n}-contact`} required minLength={5} value={form[`reference${n}_contact`]} onChange={update(`reference${n}_contact`)} className={inputCls} placeholder="Comment la joindre" data-testid={`benevole-ref${n}-contact-input`} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid gap-6 md:grid-cols-2">
                {[
                  { id: "cv", label: "Votre CV *", fichier: cv, setter: setCv, ref: cvRef, aide: "PDF ou Word, 20 Mo maximum" },
                  { id: "lettre", label: "Lettre de présentation *", fichier: lettre, setter: setLettre, ref: lettreRef, aide: "PDF ou Word, 20 Mo maximum" },
                ].map((d) => (
                  <div key={d.id}>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">{d.label}</p>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      className="hidden"
                      ref={d.ref}
                      onChange={choisirFichier(d.setter)}
                      data-testid={`benevole-${d.id}-input`}
                    />
                    <button
                      type="button"
                      onClick={() => d.ref.current?.click()}
                      data-testid={`benevole-${d.id}-button`}
                      className={`flex w-full items-center gap-3 rounded-sm border px-4 py-3.5 text-sm transition-colors duration-200 ${
                        d.fichier
                          ? "border-primary/50 bg-primary/5 text-foreground"
                          : "border-dashed border-border bg-background text-foreground/50 hover:border-primary hover:text-primary"
                      }`}
                    >
                      <FileText size={16} className={d.fichier ? "text-primary" : ""} />
                      <span className="truncate">{d.fichier ? d.fichier.name : d.aide}</span>
                    </button>
                  </div>
                ))}
              </div>
              <div>
                <label htmlFor="b-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Parlez-nous de vous *</label>
                <textarea id="b-message" required minLength={10} rows={5} value={form.message} onChange={update("message")} className={`${inputCls} resize-y`} placeholder="Votre parcours, votre expertise et ce qui vous motive…" data-testid="benevole-message-textarea" />
              </div>
              <button
                type="submit"
                disabled={envoi}
                data-testid="benevole-submit-button"
                className="group flex items-center gap-3 rounded-sm bg-primary px-8 py-4 text-sm font-semibold text-primary-foreground transition-[background-color,transform,opacity] duration-200 hover:-translate-y-0.5 hover:bg-[hsl(22,83%,40%)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {envoi ? "Envoi en cours…" : "Rejoindre la liste des bénévoles"}
                <Send size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
              </button>
              <p className="text-xs leading-relaxed text-foreground/50" data-testid="benevole-consent-note">
                En envoyant ce formulaire, vous consentez à ce que vos renseignements soient
                utilisés pour donner suite à votre candidature, conformément à notre politique de
                protection des renseignements personnels (lien dans le pied de page).
              </p>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
}
